import { CommonServiceIds } from 'azure-devops-extension-api';
import { IdentityServiceIds } from 'azure-devops-extension-api/Identities';
import { CoreRestClient } from 'azure-devops-extension-api/Core';
import { getUser, getService } from 'azure-devops-extension-sdk';

const identityService = getService(IdentityServiceIds.IdentityService);

const identityCache = {};

const loadHostUrl = async () => {
  const locationService = await getService(CommonServiceIds.LocationService);
  const hostBaseUrl = await locationService.getResourceAreaLocation(
    CoreRestClient.RESOURCE_AREA_ID
  );
  const urlObject = new URL(hostBaseUrl);
  return `${urlObject.protocol}//${urlObject.host}`;
};

let hostBaseUrlPromise;

const getHostUrl = async () => {
  hostBaseUrlPromise = hostBaseUrlPromise || loadHostUrl();
  return hostBaseUrlPromise;
};

export const getMyself = async () => {
  const me = getUser();
  const users = await getUsersById([me.id]);
  if (users.length === 0) {
    throw new Error('User not found');
  }
  return users[0];
};

const getIdentitiesFromMRU = async () => {
  const service = await identityService;
  return service
    .getIdentityMruAsync()
    .then((identities) =>
      identities.filter(
        (identity) => identity.entityType.toLowerCase() === 'user'
      )
    );
};

/**
 * fixLocalId:  Azure DevOps has a weird bug that users might return from their API with no localId.
 *              This parameter is an atempt to fix that, by searching again the user by it's entityId.
 *              When searching users by ID, it seems that the API behaves well; the problem is connected
 *              to searching users by name.
 */
export const mapIdentitiesToUsers = async (identities, fixLocalId) => {
  const hostUrl = await getHostUrl();
  return Promise.all(
    identities.map(async (identity) => {
      if (fixLocalId && !identity.localId) {
        const [fixedUser] = await getUsersById([identity.entityId]);
        if (fixedUser) return fixedUser;
      }
      return {
        id: identity.localId || identity.entityId,
        name: identity.displayName,
        photo_tiny: `${hostUrl}/${identity.image}`,
        photo_thumb: `${hostUrl}/${identity.image}`,
      };
    })
  );
};

/**
 * Fetch users quering by searchTerm
 * @param {string} searchTerm - The term to filter by
 * @returns {Promise<*>}
 */
export const getUsers = async (searchTerm = '') => {
  if (!searchTerm) {
    const mruIdentities = await getIdentitiesFromMRU();
    return await mapIdentitiesToUsers(mruIdentities);
  }

  const searchRequest = {
    query: searchTerm,
    identityTypes: ['user'],
  };
  const service = await identityService;
  const identities = await service.searchIdentitiesAsync(
    searchRequest.query,
    searchRequest.identityTypes,
    searchRequest.operationScopes,
    searchRequest.queryTypeHint,
    searchRequest.options
  );
  return await mapIdentitiesToUsers(identities);
};

export const getUsersById = async (userIds) => {
  const searchRequest = {
    identityTypes: ['user'],
    queryTypeHint: 'uid',
  };
  const service = await identityService;
  const identities = await Promise.all(
    [...new Set(userIds)].map(async (userId) => {
      return searchIdentitiesCached(userId, searchRequest, service);
    })
  );
  return await mapIdentitiesToUsers(identities.flat());
};

/*
 * Returns a promise for a user search which is already in progress or a resolved promise
 * for a user that had already been found.
 */
const searchIdentitiesCached = (userId, searchRequest, identityService) => {
  const cachedIdentity = identityCache[userId];
  if (cachedIdentity) {
    return cachedIdentity.then
      ? cachedIdentity
      : Promise.resolve(cachedIdentity);
  }
  const identitySearch = identityService
    .searchIdentitiesAsync(
      userId,
      searchRequest.identityTypes,
      searchRequest.operationScopes,
      searchRequest.queryTypeHint,
      searchRequest.options
    )
    .then((result) => {
      identityCache[userId] = result;
      return result;
    });
  identityCache[userId] = identitySearch;
  return identitySearch;
};
